import { LocalAudioTrack as TwilioLocalAudioTrack } from "twilio-video";
import { logger } from "../../../../datadog/logger";
import { CallbackError, CallbackSuccess } from "../../../../types";
import { getStoredAudioVideoSettings } from "../../../core/utils";
import {
  disableNoiseCancellation as disableNoiseCancellation_,
  enableNoiseCancellation as enableNoiseCancellation_,
} from "../utils";
import { type SetStoredAudioVideoSettings } from "../../../core/hooks/useStoredAudioVideoSettings";

export enum NoiseCancellationCallbackResponseCode {
  MISSING_TRACK = "missing_track",
  ERROR = "error",
  NOOP = "noop",
}

type NoiseCancellationCallbackResponse =
  | CallbackSuccess<
      TwilioLocalAudioTrack,
      NoiseCancellationCallbackResponseCode
    >
  | CallbackSuccess<null, NoiseCancellationCallbackResponseCode.NOOP> // for feature flag disabled
  | CallbackError<NoiseCancellationCallbackResponseCode>;

export async function enableNoiseCancellation({
  localAudioTrack,
  setStoredAudioVideoSettings,
}: {
  localAudioTrack?: TwilioLocalAudioTrack;
  setStoredAudioVideoSettings: SetStoredAudioVideoSettings;
}): Promise<NoiseCancellationCallbackResponse> {
  if (!localAudioTrack)
    return {
      status: "error",
      code: NoiseCancellationCallbackResponseCode.MISSING_TRACK,
    };
  try {
    await enableNoiseCancellation_(localAudioTrack);
  } catch (e) {
    const error = e as Error;
    logger.error("Failed to enable noise cancellation", {}, error);
    return {
      status: "error",
      code: NoiseCancellationCallbackResponseCode.ERROR,
      value: error,
    };
  }
  setStoredAudioVideoSettings((prev) => ({
    ...prev,
    noiseCancellationIsEnabled: true,
  }));
  return {
    status: "success",
    value: localAudioTrack,
  };
}

export async function disableNoiseCancellation({
  localAudioTrack,
  setStoredAudioVideoSettings,
}: {
  localAudioTrack?: TwilioLocalAudioTrack;
  setStoredAudioVideoSettings: SetStoredAudioVideoSettings;
}): Promise<NoiseCancellationCallbackResponse> {
  if (!localAudioTrack)
    return {
      status: "error",
      code: NoiseCancellationCallbackResponseCode.MISSING_TRACK,
    };
  try {
    await disableNoiseCancellation_(localAudioTrack);
  } catch (e) {
    const error = e as Error;
    logger.error("Failed to disable noise cancellation", {}, error);
    return {
      status: "error",
      code: NoiseCancellationCallbackResponseCode.ERROR,
      value: error,
    };
  }
  setStoredAudioVideoSettings((prev) => ({
    ...prev,
    noiseCancellationIsEnabled: false,
  }));
  return {
    status: "success",
    value: localAudioTrack,
  };
}

export async function initNoiseCancellation({
  localAudioTrack,
  setStoredAudioVideoSettings,
}: {
  localAudioTrack?: TwilioLocalAudioTrack;
  setStoredAudioVideoSettings: SetStoredAudioVideoSettings;
}): Promise<NoiseCancellationCallbackResponse> {
  const { noiseCancellationIsEnabled } = getStoredAudioVideoSettings();
  if (noiseCancellationIsEnabled) {
    return await enableNoiseCancellation({
      localAudioTrack,
      setStoredAudioVideoSettings,
    });
  }
  return await disableNoiseCancellation({
    localAudioTrack,
    setStoredAudioVideoSettings,
  });
}
